'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { ReplayIcon } from 'assets/icons';
import styles from './BackgroundVideo.module.css';

export type BackgroundVideoSource = {
  src: string;
  media?: string;
  type?: string;
};

export interface BackgroundVideoProps {
  src?: string;
  /**
   * When set, rendered as `<source>` children and `src` is omitted so the
   * browser can pick a file via `media` on first parse (SSR-safe).
   */
  sources?: BackgroundVideoSource[];
  poster?: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  /** When true, video will play only once when first visible and not replay on re-entry */
  playOnce?: boolean;
  /** Visibility threshold at which to play (pause below). Defaults to 0.1 */
  visibilityThreshold?: number;
  /** When true, shows a replay button overlay */
  showReplayButton?: boolean;
  /**
   * Defaults to `'metadata'` (INV-26) for ambient/viewport-gated video. The
   * homepage hero passes `'auto'`: its src only arrives after the viewport is
   * measured, so metadata-only would push the first frame a round trip later.
   */
  preload?: 'none' | 'metadata' | 'auto';
  style?: React.CSSProperties;
}

const BackgroundVideo = React.forwardRef<
  HTMLVideoElement,
  BackgroundVideoProps
>(
  (
    {
      src,
      sources,
      poster,
      autoPlay = true,
      loop = true,
      muted = true,
      playsInline = true,
      playOnce = false,
      visibilityThreshold = 0.1,
      showReplayButton = false,
      preload = 'metadata',
      style,
    },
    forwardedRef,
  ) => {
    const t = useTranslations('homepage');
    const internalRef = React.useRef<HTMLVideoElement | null>(null);
    const hasPlayedOnceRef = React.useRef<boolean>(false);

    // Use callback ref to set both internal and forwarded refs
    const setRefs = React.useCallback(
      (node: HTMLVideoElement | null) => {
        internalRef.current = node;
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          (
            forwardedRef as React.MutableRefObject<HTMLVideoElement | null>
          ).current = node;
        }
      },
      [forwardedRef],
    );

    const hasSources = Boolean(sources?.length);


    React.useEffect(() => {
      if (typeof window === 'undefined') return;
      const el = internalRef.current;
      if (!el) return;

      const onIntersect: IntersectionObserverCallback = (entries) => {
        const entry = entries[0];
        const ratio = entry.intersectionRatio;
        if (ratio < visibilityThreshold) {
          el.pause();
          return;
        }

        if (playOnce && hasPlayedOnceRef.current) {
          el.pause();
          return;
        }

        const playPromise = el.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => undefined);
        }
      };

      const observer = new IntersectionObserver(onIntersect, {
        threshold: [0, visibilityThreshold, 0.25, 0.5, 0.75, 1],
      });
      observer.observe(el);

      const handleEnded = () => {
        if (playOnce) {
          hasPlayedOnceRef.current = true;
        }
      };
      el.addEventListener('ended', handleEnded);

      return () => {
        observer.disconnect();
        el.removeEventListener('ended', handleEnded);
      };
    }, [visibilityThreshold, playOnce]);


    /**
     * WebKit can commit to a <source> before the media-gated siblings are
     * attached, settling on the unconditional fallback — on a desktop viewport
     * that means the 1920x4570 portrait file rendered into a landscape box, so
     * `object-fit: cover` crops it enormously. The media queries themselves are
     * honoured: an explicit load() re-runs selection and picks correctly.
     * Re-select once when the committed file is not the one the <source media>
     * list resolves to. No-op in browsers that already got it right.
     */
    const reselectedRef = React.useRef(false);
    React.useEffect(() => {
      const el = internalRef.current;
      if (!el || !sources?.length || reselectedRef.current) return;

      const expected = sources.find(
        (s) => !s.media || window.matchMedia(s.media).matches,
      );
      if (!expected) return;
      const want = new URL(expected.src, window.location.href).href;

      const reselect = () => {
        if (reselectedRef.current || !el.currentSrc || el.currentSrc === want) {
          return;
        }
        reselectedRef.current = true;
        // Deliberately no resume: this is an intro that must play from its
        // first frame, and the playhead belongs to the file we are replacing.
        el.load();
      };

      if (el.currentSrc) {
        reselect();
        return;
      }
      el.addEventListener('loadedmetadata', reselect, { once: true });
      return () => el.removeEventListener('loadedmetadata', reselect);
    }, [sources]);

    const handleReplay = React.useCallback(() => {
      if (internalRef.current) {
        internalRef.current.currentTime = 0;
        internalRef.current.play().catch(() => {
          // Silently handle play promise rejection
        });
      }
    }, []);

    return (
      <div className={styles.videoWrapper}>
        <video
          ref={setRefs}
          {...(hasSources ? {} : { src })}
          poster={poster}
          preload={preload}
          autoPlay={autoPlay}
          loop={playOnce ? false : loop}
          muted={muted}
          playsInline={playsInline}
          style={style}
        >
          {hasSources
            ? sources?.map((source) => (
                <source
                  key={`${source.media ?? 'default'}:${source.src}`}
                  src={source.src}
                  media={source.media}
                  type={source.type ?? 'video/mp4'}
                />
              ))
            : null}
        </video>
        {showReplayButton && (
          <button
            className={styles.replayButton}
            onClick={handleReplay}
            data-buttontype="animation replay"
            aria-label={t(
              'homepage.backgroundVideo.button.replayHeroAnimation',
            )}
            title={t('homepage.backgroundVideo.button.replayAnimation')}
          >
            <ReplayIcon fill="#ffffff" />
          </button>
        )}
      </div>
    );
  },
);

BackgroundVideo.displayName = 'BackgroundVideo';

export default BackgroundVideo;
export { BackgroundVideo };
